import { Modal, Spin, Steps } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Headers from "../../../redux/Actions/Headers/Header";

import ConfirmSMSBlastModal from "./components/ConfirmSMSBlastModal";
import CreateSMSStep1 from "./components/CreateSMSStep1";
import CreateSMSStep2 from "./components/CreateSMSStep2";

import styles from "./CreateSMS.module.scss";

import PrefixPath from "../../../config/AppConfig";
import service from "../../../auth/FetchInterceptor";
import UserData from "../../../utils/UserData";
import { normalizeRecipient, buildExcludedResidentKeys, getAvailableMessageLength, SMS_FOOTER, sortRecipientsByLastName } from "./smsWizardUtils";

const CreateSMS = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getUserEmail, getUserName } = UserData();

  const [properties, setProperties] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedCommunities, setSelectedCommunities] = useState([]);
  const [smsText, setSmsText] = useState("");
  const [wasSubmitAttempted, setWasSubmitAttempted] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientRows, setRecipientRows] = useState([]);
  const [selectedRecipientKeys, setSelectedRecipientKeys] = useState([]);
  const [step2SubmitAttempted, setStep2SubmitAttempted] = useState(false);

  const [isSubmitModalOpen, setSubmitModalOpen] = useState(false);
  const [isCancelModalOpen, setCancelModalOpen] = useState(false);

  const [smsSummary, setSmsSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const [sendingSMS, setSendingSMS] = useState(false);
  const [hasNoAvailableCommunities, setHasNoAvailableCommunities] = useState(false);

  const [isLoadingProperties, setIsLoadingProperties] = useState(true);

  useEffect(() => {
    dispatch(
      Headers.setHeader({
        title: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ArrowLeftOutlined
              style={{ cursor: "pointer", fontSize: 16 }}
              onClick={() => navigate(-1)}
            />
            <span>Create Message</span>
          </div>
        ),
        actions: [],
      })
    );

    return () => dispatch(Headers.resetHeader());
  }, [dispatch, navigate]);

  useEffect(() => {
    service
      .get(PrefixPath.SMS_API_BASE_URL + "Company/GetAllCompaniesWithCommunities", {})
      .then((response) => {
        const companies = Array.isArray(response?.value) ? response.value : [];

        if (companies.length === 0) {
          setHasNoAvailableCommunities(true);
          setProperties([]);
          return;
        }

        setHasNoAvailableCommunities(false);

        const props = companies.map((company) => ({
          PropertyKey: company.companyKey,
          PropertyName: company.companyName,
          Communities: Array.isArray(company.communities)
            ? company.communities.map((c) => ({
                CommunityKey: c.communityKey,
                CommunityName: c.communityName,
                PropertyKey: company.companyKey,
              }))
            : [],
        }));

        setProperties(props);
      })
      .catch(() => {
        setHasNoAvailableCommunities(true);
        setProperties([]);
      })
      .finally(() => {
        setIsLoadingProperties(false);
      });
  }, []);

  const communityNames = selectedProperty
    ? selectedProperty.Communities.filter((c) => selectedCommunities.includes(c.CommunityKey)).map(
        (c) => c.CommunityName
      )
    : [];

  const formattedMessage = selectedProperty?.PropertyName
    ? `${selectedProperty.PropertyName}\n${smsText}`
    : smsText;

  const maxMessageLength = getAvailableMessageLength(selectedProperty?.PropertyName);

  const handleSelectProperty = (value) => {
    if (hasNoAvailableCommunities) return;

    const property = properties.find((p) => p.PropertyName === value);
    setSelectedProperty(property);
    setSearchText(value);

    if (property?.Communities?.length) {
      const allCommunityKeys = property.Communities.map((c) => c.CommunityKey);

      setSelectedCommunities(allCommunityKeys);
    } else {
      setSelectedCommunities([]);
    }
  };

  const handlePropertyChange = (val) => {
    if (hasNoAvailableCommunities) return;

    setSearchText(val);
    if (!val) {
      setSelectedProperty(null);
      setSelectedCommunities([]);
      setSmsText("");
    }
  };

  const handleCancel = () => setCancelModalOpen(true);

  const fetchMessageSummary = (companyKey, recipientCount) => {
    const queryStr = [
      `companyKey=${encodeURIComponent(companyKey)}`,
      `recipientCount=${encodeURIComponent(recipientCount)}`,
    ].join("&");
    return service.get(`${PrefixPath.SMS_API_BASE_URL}Message/GetMessageSummary?${queryStr}`);
  };

  const fetchRecipientsByCommunities = () => {
    const queryStr = selectedCommunities
      .map((id) => `communityKeyList=${encodeURIComponent(id)}`)
      .join("&");
    return service.get(
      `${PrefixPath.SMS_API_BASE_URL}Recipient/GetRecipientsByCommunities?${queryStr}`
    );
  };

  const applySummaryResponse = (data) => {
    if (data && (data.isSuccess || data.IsSuccess)) {
      const value = data.value || {};
      setSmsSummary(value);
      return { ok: true, error: null };
    }
    setSmsSummary(null);
    return {
      ok: false,
      error: data?.message || data?.Message || "Could not load SMS summary.",
    };
  };

  const handleGoToStep2 = () => {
    if (hasNoAvailableCommunities) return;

    setWasSubmitAttempted(true);

    if (
      !selectedProperty ||
      selectedCommunities.length === 0 ||
      smsText.trim().length === 0 ||
      smsText.length > maxMessageLength
    ) {
      return;
    }

    setSummaryLoading(true);
    setSummaryError(null);
    setSmsSummary(null);
    setRecipientRows([]);
    setSelectedRecipientKeys([]);
    setRecipientSearch("");
    setStep2SubmitAttempted(false);

    fetchRecipientsByCommunities()
      .then((recipientsData) => {
        if (!(recipientsData && (recipientsData.isSuccess || recipientsData.IsSuccess))) {
          const errMsg =
            recipientsData?.message || recipientsData?.Message || "Could not load recipients.";
          setSummaryError(errMsg);
          return;
        }
        const rawList = Array.isArray(recipientsData.value) ? recipientsData.value : [];
        const rows = sortRecipientsByLastName(
          rawList.map((raw, i) => normalizeRecipient(raw, i))
        );
        setRecipientRows(rows);
        setSelectedRecipientKeys(rows.map((r) => r.key));
        setSummaryError(null);
        setCurrentStep(2);
      })
      .catch((err) => {
        setSummaryError(err?.message || "Could not load sending information.");
        setSmsSummary(null);
        setRecipientRows([]);
        setSelectedRecipientKeys([]);
      })
      .finally(() => {
        setSummaryLoading(false);
      });
  };

  const handleBackToStep1 = () => {
    setCurrentStep(1);
    setRecipientSearch("");
    setStep2SubmitAttempted(false);
    setSmsSummary(null);
    setSummaryError(null);
  };

  const handleReviewAndSend = () => {
    setStep2SubmitAttempted(true);
    if (recipientRows.length > 0 && selectedRecipientKeys.length === 0) {
      return;
    }
    if (recipientRows.length === 0) {
      return;
    }

    setSummaryLoading(true);
    setSummaryError(null);

    fetchMessageSummary(selectedProperty.PropertyKey, selectedRecipientKeys.length)
      .then((data) => {
        const result = applySummaryResponse(data);
        if (!result.ok) {
          setSummaryError(result.error);
          return;
        }
        setSubmitModalOpen(true);
      })
      .catch((err) => {
        setSummaryError(err?.message || "Could not load SMS summary.");
        setSmsSummary(null);
      })
      .finally(() => {
        setSummaryLoading(false);
      });
  };

  const confirmCancel = () => {
    setSearchText("");
    setSelectedProperty(null);
    setSelectedCommunities([]);
    setSmsText("");
    setCancelModalOpen(false);
    setWasSubmitAttempted(false);
    setStep2SubmitAttempted(false);
    setSmsSummary(null);
    setSummaryError(null);
    setSummaryLoading(false);
    setCurrentStep(1);
    setRecipientRows([]);
    setSelectedRecipientKeys([]);
    setRecipientSearch("");
  };

  const confirmSubmit = async () => {
    const companyKey = selectedProperty?.PropertyKey;
    const communitiesKeys = selectedCommunities;
    const messageContent = selectedProperty?.PropertyName ? `${selectedProperty.PropertyName}\n${smsText}${SMS_FOOTER}` : `${smsText}${SMS_FOOTER}`;
    const senderName = getUserName();
    const sender = getUserEmail();
    const costPerMessage = smsSummary?.unitPrice ?? 0;
    const estimatedTotalCost = smsSummary?.chargeToSendMessage ?? 0;

    const payload = {
      companyKey,
      companyName: selectedProperty?.PropertyName || "",
      communitiesKeys,
      messageContent,
      senderName,
      sender,
      costPerMessage,
      estimatedTotalCost,
      excludedResidentContactKeys: buildExcludedResidentKeys(recipientRows, selectedRecipientKeys),
    };

    setSendingSMS(true);
    try {
      await service.post(PrefixPath.SMS_API_BASE_URL + "Message", payload);
      setSubmitModalOpen(false);
      Modal.success({
        title: "Message sent!",
        content: "Your message was sent successfully.",
        afterClose: () => navigate("/AAM/sms"),
      });
    } catch (err) {
      Modal.error({
        title: "The message could not be sent.",
        content: err?.message || "An error occurred.",
      });
    } finally {
      setSendingSMS(false);
    }
  };

  const modalRecipientCount = selectedRecipientKeys.length;

  const isPageLoading = summaryLoading || sendingSMS || isLoadingProperties;

  return (
    <Spin spinning={isPageLoading} size="large">
      <div className={styles.container} style={{ minHeight: "60vh" }}>
        <div
          className={`${styles.wizardHeader}${
            currentStep === 2 ? ` ${styles.wizardHeaderStep2Mobile}` : ""
          }`}
        >
          <Steps
            size="small"
            current={currentStep - 1}
            items={[{ title: "Create Message" }, { title: "Filter Recipients" }]}
            className={styles.wizardSteps}
          />
        </div>

        {currentStep === 1 && (
          <CreateSMSStep1
            properties={properties}
            searchText={searchText}
            selectedProperty={selectedProperty}
            selectedCommunities={selectedCommunities}
            smsText={smsText}
            maxMessageLength={maxMessageLength}
            wasSubmitAttempted={wasSubmitAttempted}
            hasNoAvailableCommunities={hasNoAvailableCommunities}
            onPropertyChange={handlePropertyChange}
            onSelectProperty={handleSelectProperty}
            onCommunitiesChange={setSelectedCommunities}
            onSmsTextChange={setSmsText}
            onCancel={handleCancel}
            onNext={handleGoToStep2}
          />
        )}

        {currentStep === 2 && (
          <CreateSMSStep2
            summaryError={summaryError}
            summaryLoading={summaryLoading}
            recipientRows={recipientRows}
            recipientSearch={recipientSearch}
            onRecipientSearchChange={setRecipientSearch}
            selectedRecipientKeys={selectedRecipientKeys}
            onRecipientSelectionChange={(keys) =>
              setSelectedRecipientKeys(keys.map((k) => String(k)))
            }
            step2SubmitAttempted={step2SubmitAttempted}
            onBack={handleBackToStep1}
            onCancel={handleCancel}
            onReviewAndSend={handleReviewAndSend}
          />
        )}

        <ConfirmSMSBlastModal
          open={isSubmitModalOpen}
          onCancel={() => setSubmitModalOpen(false)}
          onConfirm={confirmSubmit}
          propertyName={selectedProperty ? selectedProperty.PropertyName : ""}
          communityNames={communityNames}
          message={ selectedProperty?.PropertyName ? `${selectedProperty.PropertyName}\n${smsText}${SMS_FOOTER}` : `${smsText}${SMS_FOOTER}` }
          isLoading={summaryLoading || sendingSMS}
          error={summaryError}
          recipientCount={modalRecipientCount}
          chargeToSendMessage={smsSummary?.chargeToSendMessage ?? 0}
          monthToDateCharges={smsSummary?.monthToDateCharges ?? 0}
          unitPrice={smsSummary?.unitPrice ?? 0}
          tierMinMessages={smsSummary?.tierMinMessages ?? 0}
          tierMaxMessages={smsSummary?.tierMaxMessages ?? null}
        />

        <Modal
          data-testid="sms-cancel-modal"
          open={isCancelModalOpen}
          title="Cancel"
          onOk={confirmCancel}
          onCancel={() => setCancelModalOpen(false)}
          okText="Yes, clear all"
          cancelText="No"
        >
          <p>
            This action will clear all fields and the current status. Are you sure you want to
            continue?
          </p>
        </Modal>
      </div>
    </Spin>
  );
};

export default CreateSMS;